import { Component, input, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormArray, FormControl } from '@angular/forms';
import { PanelComponent, Register } from '@xanadu/core';
import { Observable, of } from 'rxjs';
import { Node, Notice, NoticeType, PrototypeModel, Aspect, PrototypeTag, Type } from '@xanadu/model';
import { AspectCache, TypeCache } from '@xanadu/cache';
import { moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { RandomHelper, StringHelper } from '@xanadu/common';

@Component({
    selector: 'app-aspect-panel',
    templateUrl: './aspect-panel.component.html',
    styleUrls: ['./aspect-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('aspect-panel')
export class AspectPanelComponent extends PanelComponent<Aspect> {
    private readonly randomHelper = inject(RandomHelper);
    private readonly stringHelper = inject(StringHelper);
    private readonly aspectCache = inject(AspectCache);
    private readonly typeCache = inject(TypeCache);

    key = input<string>();

    tabAspect = { caption: 'Aspect', active: true, hidden: false };
    metaAuto: any = { id: 'model', name: 'Aspect', display: 'name', mandatory: true, alwaysMatch: true };
    metaGraphql: any = { id: 'graphqlType', name: 'GraphQL Type', mandatory: true, info: 'GraphQL interface name, starts with _, can contain letters, numbers and underscores only.', pattern: '^[_][a-zA-Z0-9_]*$', updateOn: 'blur' }; //id can NOT be 'key', it is a preserved word

    formArray: FormArray<any> = new FormArray<any>([]);
    types?: Map<string, Type>;
    prototypeModel?: PrototypeModel;

    tags: Map<string, PrototypeTag> = new Map<string, PrototypeTag>();

    graphqlType?: string;

    protected override get nodeType(): string | undefined {
        return 'aspect';
    }

    constructor(
    ) {
        super();

        effect(() => {
            const formGroup = this.formGroup();
            if (formGroup) {
                formGroup.setControl('fields', this.formArray);
            }
        });

        effect(() => {
            const aspect = this.node();
            this.types = this.typeCache.signal();

            if (aspect && this.types) {
                this.graphqlType = aspect.graphqlType;
                this.loadAspect(aspect, this.model().custome);
            }
        });
    }

    onAddField(tag: string) {
        let entry = this.tags.get(tag);
        if (entry && this.prototypeModel?.fields) {
            const field = { key: '__', value: { name: '', type: { input: '' }, tag, info: '' } };
            const group = new FormGroup({});
            this.prototypeModel.fields.push(field);
            entry.add({ id: this.randomHelper.generate(), field, control: new FormControl(tag), group })

            this.formArray.push(group);
            this.formGroup()?.markAsDirty();
            this.formGroup()?.updateValueAndValidity();
        }
    }

    onAddTag() {
        const tag: string = 'New Tag';
        if (!this.tags.has(tag)) {
            this.tags.set(tag, new PrototypeTag(tag));
            this.model().custome[tag] = { expanded: true };
        }
    }

    onDelete(event: any) {
        const tag = this.tags.get(event.value.tag);

        if (tag && this.prototypeModel?.fields) {
            const index = this.prototypeModel.fields.findIndex((x: any) => x === event);
            if (index >= 0) {
                this.prototypeModel.fields.splice(index, 1);
                this.formArray.removeAt(index);
                this.formGroup()?.markAsDirty();
                this.formGroup()?.updateValueAndValidity();
            }

            const tagIndex = tag.items.findIndex((x: any) => x.field === event);
            if (tagIndex >= 0) {
                tag.items.splice(tagIndex, 1);
            }
        }
    }

    onDrop(event: any) {
        let moved = false;
        if (event.previousContainer === event.container) {
            if (event.previousIndex != event.currentIndex) {
                moveItemInArray(event.container.data.items, event.previousIndex, event.currentIndex);
                moved = true;
            }
        } else {
            const tagPrevious = event.previousContainer.data;
            const tagCurrent = event.container.data;

            const item = tagPrevious.items[event.previousIndex];
            if (item) {
                item.field.value.tag = tagCurrent.tag;
                (<FormControl>item.control).patchValue(item.field.value.tag);
            }

            transferArrayItem(
                tagPrevious.items,
                tagCurrent.items,
                event.previousIndex,
                event.currentIndex,
            );
            moved = true;
        }

        if (moved) {
            this.formGroup()?.markAsDirty();
            this.formGroup()?.updateValueAndValidity();
        }
    }

    onTag(event: { tag: string, data: PrototypeTag }) {
        const prototypeTag = event.data;
        if (event.tag === prototypeTag.meta.title || this.tags.has(event.tag)) {
            const errorTag = new Notice();
            errorTag.type = NoticeType.Error;
            errorTag.text = 'Tag must be unique.';
            return;
        }

        const model = this.model();
        const value = this.tags.get(prototypeTag.meta.title);
        this.tags.delete(prototypeTag.meta.title);
        const custome = model.custome[prototypeTag.meta.title];
        model.custome[prototypeTag.meta.title] = undefined;
        prototypeTag.setTag(event.tag);
        if (value) {
            this.tags.set(event.tag, value);
        }
        model.custome[event.tag] = custome;

        const items = prototypeTag.items;
        if (items?.length && prototypeTag.meta.title?.length) {
            items.forEach(item => {
                item.field.value.tag = event.tag;
                (<FormControl>item.control).patchValue(item.field.value.tag);
            });

            this.formGroup()?.markAsDirty();
            this.formGroup()?.updateValueAndValidity();
        }
    }

    validate: (x: any) => Observable<boolean> = (value) => {
        const aspect = this.node();

        if (aspect?._id) {
            return this.dataService.checkGraphQL(aspect._id, value);
        }

        return of(false);
    }

    protected override setup(aspect: Aspect): void {
        super.setup(aspect);

        this.tabs = [this.tabAspect];
        this.tabSelected = this.tabAspect;
    }

    protected override load(aspect: Aspect, form: any): string | undefined {
        super.load(aspect, form);
        return this.copyForm(aspect, form);
    }

    protected override refresh(aspect: Aspect): void {
        super.refresh(aspect);

        this.aspectCache.update();
    }

    private copyForm(aspect: Aspect, form: any): string | undefined {
        const data: any = {};
        const set = new Set<string>();
        let duplicates: string[] = [];

        const { graphqlType, fields } = form;

        const mapFields: Map<string, { name: string, field: any }> = new Map<string, { name: string, field: any }>();

        fields.forEach((field: any) => {
            if (field.name?.length) {
                const name = this.stringHelper.toLowerCamel(field.name, true);
                if (set.has(name)) {
                    duplicates.push(name);
                } else {
                    if (field.typeId && this.types) {
                        const type = this.types.get(field.typeId)
                        if (type) {
                            field.type = type;
                            field.graphqlType = type.graphqlType;
                        }
                    }
                    mapFields.set(field.id, { name, field });
                    set.add(name);
                }
            }
        });

        this.tags.forEach(tag => {
            if (tag.items?.length) {
                tag.items.forEach((item, index) => {
                    const entry = mapFields.get(item.id);
                    if (entry?.field) {
                        entry.field.index = index;
                        entry.field.tag = tag.tag;
                        data[entry.name] = entry.field;
                    }
                });
            }
        });

        if (duplicates.length) {
            const found = duplicates.join(' ,');
            return `Protytype and Aspect field names have to be unique. Found duplicates: ${found}.`
        } else if (aspect) {
            aspect.data = data;
            aspect.graphqlType = graphqlType;
        }
    }

    private loadFields(custome: any) {
        this.tags.clear();
        this.formArray.clear();

        if (this.prototypeModel && this.prototypeModel.fields) {
            for (const field of this.prototypeModel.fields) {
                const group = new FormGroup({});
                if (field.value.tag) {
                    let entry = this.tags.get(field.value.tag);
                    if (!entry) {
                        this.tags.set(field.value.tag, entry = new PrototypeTag(field.value.tag));
                        custome[field.value.tag] = custome[field.value.tag] || { expanded: true };
                    }
                    entry.add({ id: this.randomHelper.generate(), field, control: new FormControl(field.value.tag), group })
                }
                this.formArray.push(group);
            }
        }
    }

    private loadAspect(node: Node, custome: any) {
        let aspect: Aspect | undefined = undefined;
        if (node instanceof Aspect) {
            aspect = node;
        }

        if (aspect) {
            this.prototypeModel = new PrototypeModel(aspect);
            this.loadFields(custome);
        }
    }
}
